/**
 * Copyright (C) Highway 9 Networks Inc. 2024
 */
import {
  PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
} from "@reduxjs/toolkit";
import { VMZGroupService } from "~/services/VMZService";
import { ZoneGroup } from "~/types/zone";
import type { RootState } from "..";
import { Types } from "~/constants/types";
import { createSocketPatchReducer } from "../events/socketPatch";

export const zoneGroupsAdapter = createEntityAdapter<ZoneGroup>({
  selectId: (group) => group.id!,
});

interface ZoneGroupState {
  zoneGroupsData: EntityState<ZoneGroup>;
  selectedId: string | null;
  open: boolean;
  selecteedZoneGroupIDs: string[];
  isEdit: boolean;
}

export const defaultVmzGroup: ZoneGroup = {
  name: "",
  description: "",
};

const initialState: ZoneGroupState = {
  zoneGroupsData: zoneGroupsAdapter.getInitialState(),
  selectedId: null,
  open: false,
  selecteedZoneGroupIDs: [Types.all],
  isEdit: false,
};

export const fetchZoneGroups = createAsyncThunk("vmzGroup/fetchZoneGroups", async () => {
  try {
    const vmzGroups = await VMZGroupService.getVMZGroupData();
    return vmzGroups;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

const zoneGroupSlice = createSlice({
  name: "zoneGroup",
  initialState,
  reducers: {
    /** Store selection identity only; resolve via selectors. Form edits stay in local edit context. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setSelectedZoneGroupIDs: (state, action: PayloadAction<string[]>) => {
      state.selecteedZoneGroupIDs = action.payload;
    },
    setEdit: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(zoneGroupsAdapter, (s) => s.zoneGroupsData),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZoneGroups.fulfilled, (state, action) => {
      zoneGroupsAdapter.setAll(state.zoneGroupsData, action.payload);
    });
  },
});

export const zoneGroupActions = zoneGroupSlice.actions;

export default zoneGroupSlice.reducer;

const zoneGroupEntitySelectors = zoneGroupsAdapter.getSelectors(
  (state: RootState) => state.zoneGroup.zoneGroupsData,
);

export const zoneGroupData = zoneGroupEntitySelectors.selectAll;
export const zoneGroupEntities = zoneGroupEntitySelectors.selectEntities;

export const zoneGroupCurrent = createSelector(
  [(state: RootState) => state.zoneGroup.selectedId, zoneGroupEntities],
  (selectedId, entities): ZoneGroup | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const zoneGroupOpen = (state: RootState) => state.zoneGroup.open;
export const zoneGroupSelectedIDs = (state: RootState) => state.zoneGroup.selecteedZoneGroupIDs;
export const zoneGroupEdit = (state: RootState) => state.zoneGroup.isEdit;
